import { Box, Typography } from "@mui/material";
import { resolveValue, useToasterStore } from "react-hot-toast";
import { LoadingBar, Spinner } from "./LoadingState";

/*
 * How the app shows `toast.loading(...)`: a "Loading…" pill with a spinner
 * where the toast would be, and a bar sliding across the top of the page
 * while anything is loading. Pages keep calling toast.loading / toast.remove
 * as before.
 */

// "loading...", "Loading..." and the like all read "Loading…"
const wording = (message) => {
  if (typeof message !== "string" || !message.trim()) return message || "Loading…";
  const text = message.trim();
  if (/^loading[.…]*$/i.test(text)) return "Loading…";
  const sentence = text.replace(/\.{3}$/, "…");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

export function LoadingToast({ toast }) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        maxWidth: 480,
        bgcolor: "#fff",
        borderRadius: 4,
        border: "1px solid #cfe5d8",
        boxShadow: "0 10px 30px rgba(15,23,42,0.15)",
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? "translateY(0)" : "translateY(-8px)",
        transition: "opacity 0.2s ease, transform 0.2s ease",
      }}
    >
      <Spinner />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>
          {wording(resolveValue(toast.message, toast))}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
          Please wait, this can take a few seconds.
        </Typography>
      </Box>
    </Box>
  );
}

// Across the top of the page while any loading toast is showing
export function GlobalLoadingBar() {
  const { toasts } = useToasterStore();
  const loading = toasts.some((t) => t.type === "loading" && t.visible);
  if (!loading) return null;
  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 10000, pointerEvents: "none" }}>
      <LoadingBar />
    </Box>
  );
}
